import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import MobileCallBar from './components/MobileCallBar';
import ScrollToTop from './components/ScrollToTop';
import Analytics from './components/Analytics';
import CookieConsentManager from './components/CookieConsentManager';
import WhatsAppButton from './components/WhatsAppButton';
import { ConsentProvider } from './context/ConsentContext';
import { LanguageProvider } from './context/LanguageContext';
import { PricingProvider } from './context/PricingContext';
import { CartProvider } from './context/CartContext';
import { getLanguagePrefixes } from './i18n/languages';
import Home from './pages/Home';
import './App.css';

const BookNow = lazy(() => import('./pages/BookNow'));
const Contact = lazy(() => import('./pages/Contact'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Accessibility = lazy(() => import('./pages/Accessibility'));
const Transportation = lazy(() => import('./pages/Transportation'));
const AirportTransportation = lazy(() => import('./pages/AirportTransportation'));
const LuggageStorage = lazy(() => import('./pages/LuggageStorage'));
const Tours = lazy(() => import('./pages/Tours'));
const TourDetail = lazy(() => import('./pages/TourDetail'));
const CityTourStopDetail = lazy(() => import('./pages/CityTourStopDetail'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Cart = lazy(() => import('./pages/Cart'));
const Confirmation = lazy(() => import('./pages/Confirmation'));
const KeyWest = lazy(() => import('./pages/KeyWest'));
const Guides = lazy(() => import('./pages/Guides'));
const GuideDetail = lazy(() => import('./pages/GuideDetail'));
const AdminPricing = lazy(() => import('./pages/AdminPricing'));
const FiveStarReview = lazy(() => import('./pages/FiveStarReview'));
const ThingsToDoMiami = lazy(() => import('./pages/ThingsToDoMiami'));
const MiamiBoatTours = lazy(() => import('./pages/MiamiBoatTours'));
const SeoLanding = lazy(() => import('./pages/SeoLanding'));
const NotFound = lazy(() => import('./pages/NotFound'));

const routeConfig = [
  { path: '/', component: Home },
  { path: '/book', component: BookNow },
  { path: '/contact', component: Contact },
  { path: '/privacy', component: PrivacyPolicy },
  { path: '/accessibility', component: Accessibility },
  { path: '/transportation', component: Transportation },
  { path: '/airport-transportation', component: AirportTransportation },
  { path: '/luggage-storage', component: LuggageStorage },
  { path: '/key-west', component: KeyWest },
  { path: '/guides', component: Guides },
  { path: '/guides/:guideSlug', component: GuideDetail },
  { path: '/admin', component: AdminPricing },
  { path: '/5star', component: FiveStarReview },
  { path: '/things-to-do-in-miami', component: ThingsToDoMiami },
  { path: '/miami-boat-tours', component: MiamiBoatTours },
  { path: '/miami-beach-airport-transportation', component: SeoLanding },
  { path: '/bag-storage-miami', component: SeoLanding },
  { path: '/key-west-day-trip-from-miami', component: SeoLanding },
  { path: '/miami-party-boat', component: SeoLanding },
  { path: '/everglades-airboat-tour-from-miami', component: SeoLanding },
  { path: '/miami-cruise-port-transportation', component: SeoLanding },
  { path: '/tours', component: Tours },
  { path: '/tours/miami-city-tour/stops/:stopSlug', component: CityTourStopDetail },
  { path: '/tours/:tourIdOrSlug', component: TourDetail },
  { path: '/checkout', component: Checkout },
  { path: '/cart', component: Cart },
  { path: '/confirmation', component: Confirmation },
];

const legacyRedirects = [
  { from: '/contact-us', to: '/contact' },
  { from: '/miami-tours', to: '/tours' },
  { from: '/everglades', to: '/tours/everglades-airboat-adventure' },
  { from: '/miami-boat-tour', to: '/miami-boat-tours' },
  { from: '/shop', to: '/tours' },
  { from: '/blog', to: '/guides' },
  { from: '/big-bus-tour', to: '/tours/miami-city-tour' },
  { from: '/tours/city-and-boat-tour-special', to: '/tours/city-and-boat-2-day-pass' },
  { from: '/yamaha-jet-ski', to: '/tours/miami-jet-ski-rental' },
];

function AppShell() {
  return (
    <>
      <ScrollToTop />
      <Analytics />
      <Navbar />
      <main id="main-content" className="site-main">
        <Suspense fallback={<div className="route-loading">Loading...</div>}>
          <Routes>
            {getLanguagePrefixes().flatMap((prefix) => routeConfig.map((route) => {
              const RouteComponent = route.component;
              const path = prefix
                ? route.path === '/'
                  ? prefix
                  : `${prefix}${route.path}`
                : route.path;

              return (
                <Route
                  key={`${prefix || 'default'}:${route.path}`}
                  path={path}
                  element={<RouteComponent />}
                />
              );
            }))}
            {getLanguagePrefixes().flatMap((prefix) => legacyRedirects.map((redirect) => {
              const path = prefix ? `${prefix}${redirect.from}` : redirect.from;
              const target = prefix
                ? redirect.to === '/'
                  ? prefix
                  : `${prefix}${redirect.to}`
                : redirect.to;

              return (
                <Route
                  key={`${prefix || 'default'}:redirect:${redirect.from}`}
                  path={path}
                  element={<Navigate to={target} replace />}
                />
              );
            }))}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <MobileCallBar />
      <Footer />
      <WhatsAppButton />
      <CookieConsentManager />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <ConsentProvider>
          <PricingProvider>
            <CartProvider><AppShell /></CartProvider>
          </PricingProvider>
        </ConsentProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}
